import { useEffect, useRef, useState } from "react";

import useDebouncedValue from "../useDebouncedValue";

interface UseAutosaveOptions {
	/**
	 * The current text value to watch
	 */
	value: string;

	/**
	 * Function to call when the value should be saved
	 */
	onSave: (value: string) => Promise<void> | void;

	/**
	 * Function to update the state value
	 */
	setValue: (value: string) => void;

	/**
	 * Debounce delay in milliseconds
	 * @default 1000
	 */
	delay?: number;

	/**
	 * Whether autosave is enabled
	 * @default true
	 */
	enabled?: boolean;

	/**
	 * Minimum time to show saving state in milliseconds
	 * @default 0
	 */
	minSavingTime?: number;
}

type SaveState = {
	status: "idle" | "saving" | "saved" | "error";
	error: Error | null;
};

/**
 * Hook for automatically saving text inputs after they stop changing
 * for a specified period of time. Handles all state tracking internally.
 */
const useAutosave = ({
	value,
	onSave,
	setValue,
	delay = 1000,
	enabled = true,
	minSavingTime = 0,
}: UseAutosaveOptions) => {
	// Get debounced version of the value
	const debouncedValue = useDebouncedValue(value, delay);

	// Track the last saved value
	const lastSavedValue = useRef(value);

	// Track if this is a user change vs. initial load or API update
	const isUserChange = useRef(false);

	// Ref to store save start time
	const saveStartTimeRef = useRef(0);

	// Replace individual state with a single SaveState
	const [saveState, setSaveState] = useState<SaveState>({
		status: "idle",
		error: null,
	});

	// Update lastSavedValue when value changes but isn't from user input
	useEffect(() => {
		if (!isUserChange.current) {
			lastSavedValue.current = value;
		}
	}, [value]);

	// Handle save when debounced value changes
	useEffect(() => {
		if (!enabled || saveState.status === "saving") return;

		if (isUserChange.current && debouncedValue !== lastSavedValue.current) {
			setSaveState({ status: "saving", error: null });
			saveStartTimeRef.current = Date.now();

			Promise.resolve(onSave(debouncedValue))
				.then(() => {
					lastSavedValue.current = debouncedValue;
					isUserChange.current = false;

					const updateSaveState = () => {
						setSaveState({ status: "saved", error: null });
					};

					if (minSavingTime > 0) {
						const elapsedTime = Date.now() - saveStartTimeRef.current;
						const remainingTime = Math.max(0, minSavingTime - elapsedTime);

						if (remainingTime > 0) {
							setTimeout(updateSaveState, remainingTime);
						} else {
							updateSaveState();
						}
					} else {
						updateSaveState();
					}
				})
				.catch((error: Error) => {
					setSaveState({
						status: "error",
						error: error instanceof Error ? error : new Error("An unknown error occurred while saving"),
					});
				});
		}
	}, [debouncedValue, enabled, onSave, saveState.status, minSavingTime]);

	// Hide saved state when user starts typing
	useEffect(() => {
		if (isUserChange.current) {
			setSaveState({ status: "idle", error: null });
		}
	}, [value]);

	// Return a wrapped version of setValue that tracks user changes
	const handleChange = (newValue: string) => {
		isUserChange.current = true;
		setValue(newValue);
	};

	return {
		isDirty: value !== lastSavedValue.current,
		isSaving: saveState.status === "saving",
		showSaved: saveState.status === "saved",
		showSaving: saveState.status === "saving",
		error: saveState.error,
		isError: saveState.status === "error",
		handleChange,
	};
};

export default useAutosave;
